import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Link,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  MenuItem
} from '@mui/material';
import { Link as RouterLink, useSearchParams, useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import api from '../services/authService';
import { saveSignupProgress, getSignupProgress, SignupStep, shouldRedirectToProgress } from '../utils/signupProgress';

function SignUp() {
  const [searchParams] = useSearchParams();
  const roleParam = searchParams.get('role') || 'affiliate'; // Default to affiliate for affiliate portal
  const navigate = useNavigate();
  const location = useLocation();
  
  // Restore form data from location state or saved progress
  const savedFormData = location.state?.formData;
  const savedProgress = getSignupProgress();
  const progressFormData = savedProgress?.data?.formData;
  
  const [formData, setFormData] = useState({
    email: savedFormData?.email || progressFormData?.email || '',
    password: savedFormData?.password || progressFormData?.password || '',
    name: savedFormData?.name || progressFormData?.name || '',
    username: savedFormData?.username || progressFormData?.username || ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isVerificationStep, setIsVerificationStep] = useState(
    savedFormData ? false : (
      location.state?.isVerificationStep || 
      (savedProgress?.step === SignupStep.VERIFICATION) || 
      false
    )
  );
  const [submittedEmail, setSubmittedEmail] = useState(
    savedFormData?.email || 
    location.state?.submittedEmail || 
    savedProgress?.data?.email || 
    ''
  );
  
  // Check for saved progress and redirect if needed (only for steps beyond signup)
  useEffect(() => {
    const redirect = shouldRedirectToProgress('/signup');
    if (redirect) {
      if (redirect.step === SignupStep.TERMS) {
        navigate('/terms-agreement', { state: redirect.data });
      } else if (redirect.step === SignupStep.SAMPLE_VIDEOS) {
        navigate('/sample-videos', { state: redirect.data });
      }
      // For VERIFICATION step, we already restored state above, so no redirect needed
    }
  }, [navigate]);
  
  // Username suggestions
  const [usernameSuggestions, setUsernameSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const usernameFieldRef = useRef(null);
  const suggestionsRef = useRef(null);
  const hasInitializedSuggestions = useRef(false);

  // Generate username suggestions based on name
  const generateUsernameSuggestions = (base) => {
    if (!base) return [];
    const cleanBase = base.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (cleanBase.length < 3) return [];
    
    const suggestions = [];
    const randomNumbers1 = Math.floor(Math.random() * 10000);
    const randomNumbers2 = Math.floor(Math.random() * 10000);
    suggestions.push(`${cleanBase}${randomNumbers1}`);
    suggestions.push(`${cleanBase}${randomNumbers2}`);
    return suggestions;
  };

  // Restore username suggestions if we have saved form data with a name (only on initial load)
  useEffect(() => {
    if (formData.name && !hasInitializedSuggestions.current) {
      const suggestions = generateUsernameSuggestions(formData.name);
      setUsernameSuggestions(suggestions);
      hasInitializedSuggestions.current = true;
    }
  }, [formData.name]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: value
    };
    setFormData(newFormData);
    
    // Save progress as user types (only on form step, not verification step)
    if (!isVerificationStep) {
      saveSignupProgress(SignupStep.FORM, {
        formData: newFormData,
        email: newFormData.email
      });
    }
    
    // Generate username suggestions when name changes
    if (name === 'name' && value) {
      const suggestions = generateUsernameSuggestions(value);
      setUsernameSuggestions(suggestions);
    }
    
    // Show suggestions when focusing on username field if we have suggestions
    if (name === 'username') {
      if (usernameSuggestions.length > 0 && !value) {
        setShowSuggestions(true);
      } else if (value) {
        setShowSuggestions(false);
      }
    }
  };

  const handleUsernameFocus = () => {
    if (usernameSuggestions.length > 0 && !formData.username) {
      setShowSuggestions(true);
    }
  };

  const handleUsernameSuggestionClick = (suggestion) => {
    setFormData({
      ...formData,
      username: suggestion
    });
    setShowSuggestions(false);
  };
  
  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        usernameFieldRef.current && 
        !usernameFieldRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password || !formData.name || !formData.username) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const requestBody = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        username: formData.username,
        role: roleParam
      };

      await api.post('/auth/signup', requestBody);

      setSubmittedEmail(formData.email);
      setIsVerificationStep(true);
      setSnackbarMessage('Registration successful! Please check your email to verify your account.');
      setOpenSnackbar(true);
      
      // Save progress to verification step
      saveSignupProgress(SignupStep.VERIFICATION, {
        formData: formData,
        email: formData.email
      });

    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Registration failed. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 4,
      px: 2,
      position: 'relative',
      zIndex: 1
    }}>
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <img 
            src="/icob-bhd.png" 
            alt="Boost Health Daily" 
            style={{ 
              maxWidth: '100%', 
              height: 'auto',
              maxHeight: '120px',
              objectFit: 'contain'
            }}
          />
        </Box>

        {/* Title */}
        <Typography 
          variant="h5" 
          component="h2" 
          align="center"
          sx={{ 
            fontWeight: 'bold',
            mb: 4,
            color: '#000000'
          }}
        >
          Get Started Creating Videos for BoostHealthDaily
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Paper elevation={0} sx={{ p: 0 }}>
          {isVerificationStep ? (
            <Box
              sx={{
                textAlign: 'center',
                p: 4,
                backgroundColor: '#f5f5f5',
                borderRadius: 2
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                Check your email
              </Typography>
              <Typography sx={{ mb: 3, color: '#555555' }}>
                We sent a verification link to <strong>{submittedEmail}</strong>. Please confirm your email to finish creating your account.
              </Typography>
            </Box>
          ) : (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            {/* Email */}
            <TextField
              fullWidth
              required
              id="email"
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              sx={{ mb: 2 }}
              variant="outlined"
            />

            {/* Password */}
            <TextField
              fullWidth
              required
              name="password"
              label="Password"
              type="password"
              id="password"
              value={formData.password}
              onChange={handleChange}
              sx={{ mb: 2 }}
              variant="outlined"
            />

            {/* Name */}
            <TextField
              fullWidth
              required
              name="name"
              label="Name"
              id="name"
              value={formData.name}
              onChange={handleChange}
              sx={{ mb: 2 }}
              variant="outlined"
            />

            {/* Username with suggestions */}
            <Box sx={{ position: 'relative', mb: 2 }} ref={usernameFieldRef}>
              <TextField
                fullWidth
                required
                name="username"
                label="Username"
                id="username"
                value={formData.username}
                onChange={handleChange}
                onFocus={handleUsernameFocus}
                variant="outlined"
              />
              {showSuggestions && usernameSuggestions.length > 0 && (
                <Paper
                  ref={suggestionsRef}
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    mt: 0.5,
                    maxHeight: 200,
                    overflow: 'auto',
                    boxShadow: 3
                  }}
                >
                  {usernameSuggestions.map((suggestion, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => handleUsernameSuggestionClick(suggestion)}
                      sx={{
                        borderBottom: index < usernameSuggestions.length - 1 ? '1px solid #e0e0e0' : 'none'
                      }}
                    >
                      {suggestion}
                    </MenuItem>
                  ))}
                </Paper>
              )}
            </Box>

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              disabled={loading || !formData.email || !formData.password || !formData.name || !formData.username}
              sx={{
                backgroundColor: '#1976d2',
                borderRadius: 1,
                mb: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                px: 2,
                py: 1.5,
                minHeight: '48px',
                width: '100%',
                border: 'none',
                cursor: loading || !formData.email || !formData.password || !formData.name || !formData.username ? 'not-allowed' : 'pointer',
                opacity: loading || !formData.email || !formData.password || !formData.name || !formData.username ? 0.7 : 1,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: loading || !formData.email || !formData.password || !formData.name || !formData.username ? '#1976d2' : '#1565c0'
                },
                '&:disabled': {
                  cursor: 'not-allowed',
                  backgroundColor: '#cccccc'
                }
              }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: '#ffffff' }} />
              ) : (
                <Typography
                  sx={{
                    color: '#ffffff',
                    fontWeight: 'bold',
                    fontSize: '1rem'
                  }}
                >
                  submit
                </Typography>
              )}
            </Button>

            {/* Already have account link */}
            <Box sx={{ textAlign: 'center' }}>
              <Link 
                component={RouterLink} 
                to="/login" 
                sx={{ 
                  color: '#1976d2',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                I already have an account
              </Link>
            </Box>
          </Box>
          )}
        </Paper>
      </Container>
      
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message={snackbarMessage}
      />
    </Box>
  );
}

export default SignUp;

